import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRoles } from "@/hooks/useUserRoles";
import { ShieldAlert } from "lucide-react";

const PlatformAdminRoute = ({ children }: { children: ReactNode }) => {
  const { session, loading: authLoading } = useAuth();
  const { isPlatformAdmin, isLoading: rolesLoading } = useUserRoles();

  if (authLoading || (session && rolesLoading)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (!session) return <Navigate to="/auth" replace />;

  if (!isPlatformAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center space-y-4 rounded-2xl border border-destructive/30 bg-destructive/5 p-8">
          <div className="mx-auto h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
            <ShieldAlert className="h-6 w-6 text-destructive" />
          </div>
          <h1 className="text-xl font-semibold">Access denied</h1>
          <p className="text-sm text-muted-foreground">
            The Platform Admin console is restricted to super-admin users. If you believe
            this is a mistake, contact the platform owner.
          </p>
          <a
            href="/app"
            className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Return to dashboard
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default PlatformAdminRoute;
